import type { OverlayEntry } from "@/components/modal/overlay.types";
import { lockDocumentScroll } from "@/components/modal/overlay-scroll-lock";

export class OverlayManager {
  private registrations = new Map<string, symbol>();
  private scrollLockCount = 0;
  private restoreScroll: (() => void) | null = null;
  private entries: readonly OverlayEntry[] = [];
  private listeners = new Set<() => void>();
  private escapeOwners = new WeakMap<Event, string | undefined>();
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  getSnapshot = () => this.entries;
  private publish(entries: readonly OverlayEntry[]) {
    // Subscribers retain snapshots, so updates replace the array.
    this.entries = entries;
    this.listeners.forEach((listener) => listener());
  }
  register(entry: OverlayEntry) {
    const token = Symbol();
    this.registrations.set(entry.id, token);
    const existingIndex = this.entries.findIndex((item) => item.id === entry.id);
    const nextEntries = [...this.entries];
    // Child layout effects run first. Insert an ancestor before its already-mounted children.
    const firstChildIndex = nextEntries.findIndex((item) => item.parentId === entry.id);
    if (existingIndex >= 0) nextEntries[existingIndex] = entry;
    else nextEntries.splice(firstChildIndex < 0 ? nextEntries.length : firstChildIndex, 0, entry);
    this.publish(nextEntries);
    return () => {
      // A stale cleanup must not unregister a newer mount with the same ID.
      if (this.registrations.get(entry.id) !== token) return;
      this.registrations.delete(entry.id);
      this.publish(this.entries.filter((item) => item.id !== entry.id));
      // Defer cleanup so Strict Mode's immediate remount can reclaim the parent ID.
      queueMicrotask(() => {
        if (this.registrations.has(entry.id)) return;
        const descendants = this.getDescendants(entry.id);
        if (!descendants.length) return;
        const removedIds = new Set(descendants.map((item) => item.id));
        this.publish(this.entries.filter((item) => !removedIds.has(item.id)));
        descendants
          .slice()
          .reverse()
          .forEach((item) => item.onClose());
      });
    };
  }
  private getDescendants(parentId: string) {
    const ancestorIds = new Set([parentId]);
    const descendants: OverlayEntry[] = [];
    // Registration keeps parents before children, so one forward pass finds every descendant.
    for (const entry of this.entries) {
      if (entry.parentId != null && ancestorIds.has(entry.parentId)) {
        ancestorIds.add(entry.id);
        descendants.push(entry);
      }
    }
    return descendants;
  }

  dismissDescendants(id: string) {
    this.getDescendants(id)
      .reverse()
      .forEach((entry) => entry.onClose());
  }

  acquireScrollLock = () => {
    if (this.scrollLockCount++ === 0) this.restoreScroll = lockDocumentScroll();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      if (--this.scrollLockCount === 0) {
        this.restoreScroll?.();
        this.restoreScroll = null;
      }
    };
  };
  update(
    id: string,
    options: Pick<
      OverlayEntry,
      "blocking" | "closeOnEscape" | "backdropClassName" | "inheritBackdrop" | "closing"
    >,
  ) {
    const entry = this.entries.find((item) => item.id === id);
    if (
      entry &&
      (entry.blocking !== options.blocking ||
        entry.closeOnEscape !== options.closeOnEscape ||
        !!entry.closing !== !!options.closing ||
        entry.backdropClassName !== options.backdropClassName ||
        !!entry.inheritBackdrop !== !!options.inheritBackdrop)
    ) {
      this.publish(this.entries.map((item) => (item.id === id ? { ...item, ...options } : item)));
    }
  }
  captureEscapeOwner = (event: KeyboardEvent) => {
    if (event.key === "Escape") this.escapeOwners.set(event, this.entries.at(-1)?.id);
  };

  isEscapeOwner(event: KeyboardEvent, id: string) {
    // Freeze the owner before dismissal so one Escape cannot also close its parent.
    return this.escapeOwners.get(event) === id;
  }
}
